/**
 * Types for Conversion Setup Wizard.
 *
 * Scope: bare-bones C.65 ceremony — create a website ConversionAction in
 * Google Ads and surface the gtag snippet. No GTM-specific UX, no
 * install-method choice, no URL scanning.
 */

export type ConversionCategory =
  | 'PURCHASE'
  | 'SUBMIT_LEAD_FORM'
  | 'SIGNUP'
  | 'ADD_TO_CART'
  | 'PAGE_VIEW'
  | 'CONTACT';

export type ConversionType = 'WEBPAGE' | 'WEBPAGE_ONCLICK';

export type CountingType = 'ONE_PER_CLICK' | 'MANY_PER_CLICK';

export interface ConversionFormData {
  // Step 1: What to track
  category: ConversionCategory;

  // Step 2: Details
  name: string;
  defaultValue: string;
  useDefaultValue: boolean;
  countingType: CountingType;
}

export interface ConversionFormErrors {
  category?: string;
  name?: string;
  defaultValue?: string;
}

export interface TagSnippet {
  type: string;
  page_format: string;
  global_site_tag: string;
  event_snippet: string;
}

export interface CreateConversionResponse {
  success: boolean;
  conversion_action_id: string;
  resource_name: string;
  name: string;
  tag_snippets: TagSnippet[];
  message?: string;
}

export interface ConversionSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  googleAdsAccountId: string;
  onSuccess?: () => void;
}

export const CONVERSION_CATEGORIES: Array<{
  value: ConversionCategory;
  label: string;
  description: string;
  icon: string;
}> = [
  {
    value: 'PURCHASE',
    label: 'Purchase',
    description: 'Customer completes a purchase or transaction',
    icon: '🛒',
  },
  {
    value: 'SUBMIT_LEAD_FORM',
    label: 'Lead Form',
    description: 'Form submission, quote request, or contact form',
    icon: '📋',
  },
  {
    value: 'SIGNUP',
    label: 'Sign-up',
    description: 'Account creation or newsletter subscription',
    icon: '✅',
  },
  {
    value: 'CONTACT',
    label: 'Contact',
    description: 'Phone call, email, or chat initiated',
    icon: '📞',
  },
  {
    value: 'ADD_TO_CART',
    label: 'Add to Cart',
    description: 'Product added to shopping cart',
    icon: '🛍️',
  },
  {
    value: 'PAGE_VIEW',
    label: 'Page View',
    description: 'Visit to a specific important page',
    icon: '👁️',
  },
];

export const COUNTING_OPTIONS: Array<{
  value: CountingType;
  label: string;
  description: string;
  recommended: ConversionCategory[];
}> = [
  {
    value: 'ONE_PER_CLICK',
    label: 'One',
    description: 'Count only one conversion per ad click (best for leads)',
    recommended: ['SUBMIT_LEAD_FORM', 'SIGNUP', 'CONTACT'],
  },
  {
    value: 'MANY_PER_CLICK',
    label: 'Every',
    description: 'Count every conversion after an ad click (best for purchases)',
    recommended: ['PURCHASE', 'ADD_TO_CART'],
  },
];

export const DEFAULT_FORM_DATA: ConversionFormData = {
  category: 'PURCHASE',
  name: '',
  defaultValue: '',
  useDefaultValue: false,
  countingType: 'ONE_PER_CLICK',
};

export const WIZARD_STEPS = [
  { label: 'Type', description: 'What to track' },
  { label: 'Details', description: 'Conversion settings' },
  { label: 'Code', description: 'Get tracking code' },
];
